/**
 * useScheduleEvents Hook
 *
 * Real-time listener for schedule events from Firestore 'scheduleEvents' collection.
 * Provides CRUD operations for events including recurring series management.
 *
 * Firestore Collection: scheduleEvents
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  where,
  getDoc,
  getDocs,
  writeBatch,
  arrayUnion,
  deleteField,
  type WriteBatch,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { RecurringRule, ScheduleEvent } from '@/types/schema';
import { getTenantIdSync, tenantIdReadUnion } from '@/hooks/useTenantId';
import {
  formatDateStr,
  parseDateStr,
  datesForRule,
  generatedDateOf,
  generationWindow,
  instancesToRemove,
} from '@/utils/recurring-schedule';

// ═══════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════

/** Add days to a date */
function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/** Check if a date is a weekday */
function isWeekday(date: Date): boolean {
  const day = date.getDay();
  return day >= 1 && day <= 5;
}

/**
 * Every weekday (Mon–Fri) in the inclusive span, as YYYY-MM-DD built from local
 * components. Empty when the span is reversed or either end is unparseable.
 */
export function weekdayDatesInSpan(startDate: string, endDate: string): string[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) return [];
  const end = parseDateStr(endDate);
  const current = parseDateStr(startDate);
  const dates: string[] = [];
  while (current <= end) {
    if (isWeekday(current)) dates.push(formatDateStr(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

// ═══════════════════════════════════════
// HOOK
// ═══════════════════════════════════════

export function useScheduleEvents() {
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Tenant isolation — REQUIRED by the rules lock (scheduleEvents is
    // tenant-isolated; an unfiltered list is rejected wholesale). Single-field
    // `in` filter (no orderBy) needs no composite index. tenantIdReadUnion is a
    // no-op today (legacy alias dead) but kept as policy insurance per CLAUDE.md.
    // Events with no tenantId never match: `npm run backfill:users-schedule-tenant
    // -- --apply` (tenant-coverage.yml `users-schedule-apply`) stamps them.
    const q = query(
      collection(db, 'scheduleEvents'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync()))
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as ScheduleEvent[];

        setEvents(data);
        setLoading(false);
      },
      (err) => {
        console.error('[useScheduleEvents] Error:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { events, loading };
}

/**
 * Whether an event is part of a group (recurring series, multi-day leave/sick
 * block, or multi-day tentative hold). Returns the group's template id for convenience.
 */
export function getEventGroupInfo(event: ScheduleEvent): {
  belongsToGroup: boolean;
  templateId: string | undefined;
  isLeaveOrSickGroup: boolean;
  isTentativeGroup: boolean;
} {
  const templateId = event.isTemplate ? event.id : event.recurringTemplateId;
  const belongsToGroup = !!templateId;
  const isLeaveOrSickGroup = belongsToGroup && (event.type === 'leave' || event.type === 'sick');
  const isTentativeGroup = belongsToGroup && event.type === 'tentative_job';
  return { belongsToGroup, templateId, isLeaveOrSickGroup, isTentativeGroup };
}

// ═══════════════════════════════════════
// CRUD FUNCTIONS
// ═══════════════════════════════════════

/** Strip undefined values — Firestore rejects them */
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as T;
}

/** Create a single schedule event */
export async function createEvent(event: Partial<ScheduleEvent>): Promise<string> {
  const now = new Date().toISOString();
  const docData = stripUndefined({
    ...event,
    isRecurring: event.isRecurring ?? false,
    isTemplate: event.isTemplate ?? false,
    techIds: event.techIds ?? [],
    tenantId: event.tenantId ?? getTenantIdSync(),
    createdAt: now,
    updatedAt: now,
  });
  const docRef = await addDoc(collection(db, 'scheduleEvents'), docData);
  return docRef.id;
}

/** Update a schedule event */
export async function updateEvent(id: string, updates: Partial<ScheduleEvent>): Promise<void> {
  await updateDoc(doc(db, 'scheduleEvents', id), stripUndefined({
    ...updates,
    updatedAt: new Date().toISOString(),
  }));
}

/** Delete a schedule event */
export async function deleteEvent(id: string): Promise<void> {
  await deleteDoc(doc(db, 'scheduleEvents', id));
}

/** Every instance doc of a series/group, tenant-scoped. */
function instancesOfTemplate(templateId: string) {
  return getDocs(
    query(
      collection(db, 'scheduleEvents'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
      where('recurringTemplateId', '==', templateId)
    )
  );
}

/**
 * The recurring template an instance belongs to, or null when it has none —
 * multi-day leave/sick blocks share `recurringTemplateId` but carry no
 * `recurringRule`, and must keep their plain delete/update behaviour.
 */
async function recurringTemplateOf(event: ScheduleEvent): Promise<ScheduleEvent | null> {
  if (event.isTemplate || !event.recurringTemplateId) return null;
  // Under the rules lock a get on a DELETED template is denied (no doc → no
  // tenantId to match) rather than returning "not found" — treat it as gone so
  // the occurrence keeps its plain delete/update. Anything else rethrows.
  const snap = await getDoc(doc(db, 'scheduleEvents', event.recurringTemplateId)).catch(
    (err: unknown) => {
      if ((err as { code?: unknown } | null)?.code === 'permission-denied') return null;
      throw err;
    }
  );
  if (!snap || !snap.exists()) return null;
  const template = { id: snap.id, ...snap.data() } as ScheduleEvent;
  return template.recurringRule ? template : null;
}

/**
 * Delete one event. For an occurrence of a recurring series the date is also
 * recorded in the template's `recurringRule.exceptions` (same batch) — without
 * it the scheduler's lazy generator re-creates the occurrence straight away.
 */
export async function deleteOccurrence(event: ScheduleEvent): Promise<void> {
  const template = await recurringTemplateOf(event);
  if (!template) {
    await deleteEvent(event.id);
    return;
  }
  const batch = writeBatch(db);
  batch.update(doc(db, 'scheduleEvents', template.id), {
    'recurringRule.exceptions': arrayUnion(generatedDateOf(template.id, event.id) ?? event.date),
    updatedAt: new Date().toISOString(),
  });
  batch.delete(doc(db, 'scheduleEvents', event.id));
  await batch.commit();
}

/**
 * Update one event. When an occurrence of a recurring series moves to another
 * day, the day it was generated for becomes an exception (same batch) so the
 * generator doesn't put it back — onto the same deterministic doc id, which
 * would snap the moved occurrence back.
 */
export async function updateOccurrence(
  event: ScheduleEvent,
  updates: Partial<ScheduleEvent>
): Promise<void> {
  const dateChanged = updates.date !== undefined && updates.date !== event.date;
  const template = dateChanged ? await recurringTemplateOf(event) : null;
  if (!template) {
    await updateEvent(event.id, updates);
    return;
  }
  const now = new Date().toISOString();
  const batch = writeBatch(db);
  batch.update(doc(db, 'scheduleEvents', template.id), {
    'recurringRule.exceptions': arrayUnion(generatedDateOf(template.id, event.id) ?? event.date),
    updatedAt: now,
  });
  batch.update(doc(db, 'scheduleEvents', event.id), stripUndefined({ ...updates, updatedAt: now }));
  await batch.commit();
}

/** Delete a recurring series: the template AND all instances referencing it */
export async function deleteRecurringSeries(templateId: string): Promise<void> {
  const batch = writeBatch(db);

  // Delete the template itself
  batch.delete(doc(db, 'scheduleEvents', templateId));

  // Find and delete all instances that reference this template
  const snapshot = await instancesOfTemplate(templateId);
  snapshot.docs.forEach((d) => {
    batch.delete(doc(db, 'scheduleEvents', d.id));
  });

  await batch.commit();
}

/** Firestore batches are limited to 500 writes */
const BATCH_SIZE = 500;

/**
 * Update a recurring series: update the template, then update all future instances.
 *
 * With `rule`, the recurrence itself changed (days, start, end) or a one-off is
 * becoming a series. The rule is written to the template first — field by field,
 * so `exceptions` already recorded survive without a read-modify-write — then
 * future occurrences the rule no longer produces are removed, and the missing
 * ones generated. Order matters: the scheduler's lazy generator reads the
 * template, so it must see the new rule before anything is deleted.
 */
export async function updateRecurringSeries(
  templateId: string,
  updates: Partial<ScheduleEvent>,
  rule?: Omit<RecurringRule, 'exceptions'>
): Promise<void> {
  const now = new Date().toISOString();
  const todayStr = formatDateStr(new Date());
  const templateRef = doc(db, 'scheduleEvents', templateId);

  // Update the template
  await updateDoc(templateRef, {
    ...updates,
    ...(rule
      ? {
          'recurringRule.frequency': rule.frequency,
          'recurringRule.dayOfWeek': rule.dayOfWeek ?? [],
          'recurringRule.startDate': rule.startDate,
          'recurringRule.endDate': rule.endDate ?? deleteField(),
        }
      : {}),
    updatedAt: now,
  });

  // Find all instances that reference this template with date >= today
  const snapshot = await instancesOfTemplate(templateId);

  let removeIds = new Set<string>();
  let template: ScheduleEvent | null = null;
  if (rule) {
    const fresh = await getDoc(templateRef);
    template = { id: fresh.id, ...fresh.data() } as ScheduleEvent;
    removeIds = new Set(
      instancesToRemove(
        templateId,
        snapshot.docs.map((d) => ({ id: d.id, date: d.data().date as string })),
        template.recurringRule ?? { ...rule, exceptions: [] },
        todayStr
      )
    );
  }

  const writes: Array<(batch: WriteBatch) => void> = [];

  snapshot.docs.forEach((d) => {
    const data = d.data();
    const ref = doc(db, 'scheduleEvents', d.id);
    if (removeIds.has(d.id)) {
      writes.push((batch) => batch.delete(ref));
      return;
    }
    // Only update future instances (date >= today)
    if (data.date >= todayStr) {
      // Update with relevant fields (preserve date, update title/description/times/techs)
      const instanceUpdates: Record<string, unknown> = { updatedAt: now };
      if (updates.title !== undefined) instanceUpdates.title = updates.title;
      if (updates.description !== undefined) instanceUpdates.description = updates.description;
      if (updates.startTime !== undefined) instanceUpdates.startTime = updates.startTime;
      if (updates.endTime !== undefined) instanceUpdates.endTime = updates.endTime;
      if (updates.techIds !== undefined) instanceUpdates.techIds = updates.techIds;
      if (updates.color !== undefined) instanceUpdates.color = updates.color;
      if (updates.type !== undefined) instanceUpdates.type = updates.type;
      // Meeting-specific fields propagate to future instances too.
      if (updates.location !== undefined) instanceUpdates.location = updates.location;
      if (updates.attendeeNames !== undefined) instanceUpdates.attendeeNames = updates.attendeeNames;
      if (updates.allAttendees !== undefined) instanceUpdates.allAttendees = updates.allAttendees;
      // Tentative-hold fields — a whole-hold edit re-points every day at the
      // new customer / likely job type.
      if (updates.customerName !== undefined) instanceUpdates.customerName = updates.customerName;
      if (updates.customerId !== undefined) instanceUpdates.customerId = updates.customerId;
      if (updates.customerPhone !== undefined) instanceUpdates.customerPhone = updates.customerPhone;
      if (updates.jobTypeHint !== undefined) instanceUpdates.jobTypeHint = updates.jobTypeHint;

      writes.push((batch) => batch.update(ref, instanceUpdates));
    }
  });

  for (let i = 0; i < writes.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    writes.slice(i, i + BATCH_SIZE).forEach((write) => write(batch));
    await batch.commit();
  }

  // Fill in the occurrences the new rule produces from today onward. Later
  // weeks are generated lazily as the scheduler navigates.
  const span = template?.recurringRule ? generationWindow(template.recurringRule, todayStr) : null;
  if (template && span) {
    await generateRecurringInstances(template, span.from, span.to);
  }
}

/**
 * Generate recurring instances from a template for a date range.
 * Each instance has isTemplate: false, recurringTemplateId pointing to the template.
 * Skips dates in exceptions[].
 * Writes all instances to Firestore in a batched write.
 */
export async function generateRecurringInstances(
  template: ScheduleEvent,
  fromDate: string,
  toDate: string
): Promise<string[]> {
  if (!template.recurringRule) {
    console.warn('[generateRecurringInstances] No recurringRule on template');
    return [];
  }

  const dates = datesForRule(template.recurringRule, fromDate, toDate);
  if (dates.length === 0) return [];

  // Check for existing instances to avoid duplicates.
  // NOTE: we ALSO use deterministic doc IDs below (`${templateId}_${dateStr}`),
  // which makes the write idempotent — if two generations race (e.g. the
  // modal's initial 3-month write + the scheduler's on-navigation lazy write),
  // both land on the same doc ID instead of creating duplicates.
  // A date is taken when its deterministic doc exists (even if that occurrence
  // was moved to another day — rewriting it would snap it back), or when a
  // legacy random-id instance sits on it.
  const existingSnapshot = await instancesOfTemplate(template.id);
  const taken = new Set<string>();
  existingSnapshot.docs.forEach((d) => {
    taken.add(generatedDateOf(template.id, d.id) ?? (d.data().date as string));
  });

  const newDates = dates.filter((d) => !taken.has(d));
  if (newDates.length === 0) return [];

  const now = new Date().toISOString();
  const ids: string[] = [];

  for (let i = 0; i < newDates.length; i += BATCH_SIZE) {
    const chunk = newDates.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);

    for (const dateStr of chunk) {
      const instanceId = `${template.id}_${dateStr}`;
      const instanceRef = doc(db, 'scheduleEvents', instanceId);
      batch.set(instanceRef, {
        type: template.type,
        title: template.title,
        description: template.description || null,
        techIds: template.techIds,
        date: dateStr,
        startTime: template.startTime || null,
        endTime: template.endTime || null,
        isRecurring: true,
        recurringTemplateId: template.id,
        isTemplate: false,
        leaveType: template.leaveType || null,
        color: template.color || null,
        // Meeting-specific fields carried onto every instance so a recurring
        // team meeting keeps its location + attendee display + all-hands flag.
        location: template.location ?? null,
        attendeeNames: template.attendeeNames ?? null,
        allAttendees: template.allAttendees ?? null,
        tenantId: template.tenantId ?? getTenantIdSync(),
        createdAt: now,
        createdBy: template.createdBy,
        updatedAt: now,
      });
      ids.push(instanceId);
    }

    await batch.commit();
  }

  return ids;
}

/**
 * Generate multi-day leave/sick instances.
 * Creates one ScheduleEvent per day, grouped by a shared templateId.
 * Skips weekends.
 */
export async function generateMultiDayLeaveInstances(
  baseEvent: Partial<ScheduleEvent>,
  startDate: string,
  endDate: string
): Promise<{ templateId: string; instanceIds: string[] }> {
  const now = new Date().toISOString();
  const createdBy = baseEvent.createdBy || localStorage.getItem('user_id') || '';

  // Create the template document first
  const tenantId = baseEvent.tenantId ?? getTenantIdSync();
  const templateRef = await addDoc(collection(db, 'scheduleEvents'), stripUndefined({
    ...baseEvent,
    date: startDate,
    isRecurring: false,
    isTemplate: true,
    leaveStartDate: startDate,
    leaveEndDate: endDate,
    tenantId,
    createdAt: now,
    createdBy,
    updatedAt: now,
  }));

  const templateId = templateRef.id;
  const ids: string[] = [];

  // Generate one instance per day (skip weekends)
  const start = parseDateStr(startDate);
  const end = parseDateStr(endDate);
  const current = new Date(start);

  const batch = writeBatch(db);

  while (current <= end) {
    if (isWeekday(current)) {
      const dateStr = formatDateStr(current);
      const instanceRef = doc(collection(db, 'scheduleEvents'));
      batch.set(instanceRef, {
        type: baseEvent.type || 'leave',
        title: baseEvent.title || 'Leave',
        description: baseEvent.description || null,
        techIds: baseEvent.techIds || [],
        date: dateStr,
        startTime: null,
        endTime: null,
        isRecurring: false,
        recurringTemplateId: templateId,
        isTemplate: false,
        leaveType: baseEvent.leaveType || null,
        leaveStartDate: startDate,
        leaveEndDate: endDate,
        color: baseEvent.color || null,
        tenantId,
        createdAt: now,
        createdBy,
        updatedAt: now,
      });
      ids.push(instanceRef.id);
    }
    current.setDate(current.getDate() + 1);
  }

  await batch.commit();

  return { templateId, instanceIds: ids };
}

/**
 * Generate a multi-day tentative hold: a hidden template (no recurringRule, so
 * the recurring generator and RecurringEventsPanel ignore it) plus one
 * `tentative_job` instance per weekday in the span, all in one batch. Every
 * reader of scheduleEvents skips `isTemplate` docs, so only the days render.
 */
export async function generateMultiDayTentativeHold(
  baseEvent: Partial<ScheduleEvent>,
  startDate: string,
  endDate: string
): Promise<{ templateId: string; instanceIds: string[] }> {
  const now = new Date().toISOString();
  const createdBy = baseEvent.createdBy || '';
  const tenantId = baseEvent.tenantId ?? getTenantIdSync();
  const dates = weekdayDatesInSpan(startDate, endDate);

  const batch = writeBatch(db);
  const templateRef = doc(collection(db, 'scheduleEvents'));
  batch.set(templateRef, stripUndefined({
    ...baseEvent,
    type: 'tentative_job' as const,
    date: dates[0] ?? startDate,
    techIds: baseEvent.techIds ?? [],
    isRecurring: false,
    isTemplate: true,
    tenantId,
    createdAt: now,
    createdBy,
    updatedAt: now,
  }));

  const instanceIds: string[] = [];
  for (const dateStr of dates) {
    const instanceRef = doc(collection(db, 'scheduleEvents'));
    batch.set(instanceRef, stripUndefined({
      type: 'tentative_job' as const,
      title: baseEvent.title || baseEvent.customerName || 'Tentative slot',
      description: baseEvent.description,
      techIds: baseEvent.techIds ?? [],
      date: dateStr,
      startTime: baseEvent.startTime,
      endTime: baseEvent.endTime,
      customerName: baseEvent.customerName,
      customerId: baseEvent.customerId,
      customerPhone: baseEvent.customerPhone,
      jobTypeHint: baseEvent.jobTypeHint,
      isRecurring: false,
      recurringTemplateId: templateRef.id,
      isTemplate: false,
      tenantId,
      createdAt: now,
      createdBy,
      updatedAt: now,
    }));
    instanceIds.push(instanceRef.id);
  }

  await batch.commit();
  return { templateId: templateRef.id, instanceIds };
}

/**
 * Lazy generation: check if instances exist for a week and generate if needed.
 * Called when the scheduler navigates to a new week.
 */
export async function ensureRecurringInstancesForWeek(
  templates: ScheduleEvent[],
  weekStartDate: string
): Promise<void> {
  const weekStart = parseDateStr(weekStartDate);
  const weekEnd = addDays(weekStart, 4); // Mon-Fri
  const weekEndStr = formatDateStr(weekEnd);

  for (const template of templates) {
    if (!template.isTemplate || !template.recurringRule) continue;

    // Check if rule's end date is before this week
    if (template.recurringRule.endDate && template.recurringRule.endDate < weekStartDate) continue;

    // Check if rule's start date is after this week
    if (template.recurringRule.startDate > weekEndStr) continue;

    await generateRecurringInstances(template, weekStartDate, weekEndStr);
  }
}

/**
 * Delete a multi-day leave group: the template AND all instances.
 * Same as deleteRecurringSeries but semantically for leave blocks.
 */
export async function deleteLeaveGroup(templateId: string): Promise<void> {
  return deleteRecurringSeries(templateId);
}
